import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        success: "border-transparent bg-emerald-100 text-emerald-700",
        warning: "border-transparent bg-amber-100 text-amber-700",
        // Document type badges - Colorful with distinct identities
        invoice: "border-transparent bg-blue-500 text-white",
        contract: "border-transparent bg-purple-500 text-white",
        tax: "border-transparent bg-amber-500 text-white",
        insurance: "border-transparent bg-emerald-500 text-white",
        identity: "border-transparent bg-orange-500 text-white",
        receipt: "border-transparent bg-teal-500 text-white",
        quote: "border-transparent bg-cyan-500 text-white",
        payslip: "border-transparent bg-indigo-500 text-white",
        attestation: "border-transparent bg-violet-500 text-white",
        bank_statement: "border-transparent bg-sky-500 text-white",
        medical_certificate: "border-transparent bg-rose-500 text-white",
        notarial_deed: "border-transparent bg-stone-600 text-white",
        administrative: "border-transparent bg-zinc-500 text-white",
        warranty: "border-transparent bg-lime-500 text-white",
        other: "border-transparent bg-slate-400 text-white",
        // Source badges
        gmail: "border-transparent bg-red-500 text-white",
        outlook: "border-transparent bg-blue-600 text-white",
        whatsapp: "border-transparent bg-green-500 text-white",
        upload: "border-transparent bg-slate-500 text-white",
        // Status badges
        processing: "border-transparent bg-amber-100 text-amber-700",
        complete: "border-transparent bg-emerald-100 text-emerald-700",
        failed: "border-transparent bg-red-100 text-red-700",
        // Expiration urgency badges
        expired: "border-transparent bg-red-500 text-white",
        urgent: "border-transparent bg-red-100 text-red-700 border-red-200",
        expiring: "border-transparent bg-amber-100 text-amber-700 border-amber-200",
        upcoming: "border-transparent bg-blue-100 text-blue-700 border-blue-200",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);
// Re-export emoji helpers from centralized mapping
import { TYPE_CONFIG, CATEGORY_CONFIG } from "@/constants/ui-mapping";

// Legacy typeEmojis for backward compatibility - now uses centralized config
export const typeEmojis: Record<string, string> = {
  ...Object.fromEntries(Object.entries(TYPE_CONFIG).map(([k, v]) => [k, v.emoji])),
  ...Object.fromEntries(Object.entries(CATEGORY_CONFIG).map(([k, v]) => [k, v.emoji])),
};

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  showEmoji?: boolean;
  docType?: string;
}

function Badge({ className, variant, showEmoji = false, docType, children, ...props }: BadgeProps) {
  const emoji = docType ? typeEmojis[docType] : variant ? typeEmojis[variant as string] : null;
  
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {showEmoji && emoji && <span>{emoji}</span>}
      {children}
    </div>
  );
}

export { Badge, badgeVariants };
